// Force rebuild: 2025-08-13T10:57:06-04:00
import React from 'react';

const EfficiencyGauge = ({ score, label, size = 200, strokeWidth = 20, t }) => {
  // ===================================================================================
  //  LOGIC & CALCULATIONS
  //  Handles the data transformations needed to render the gauge.
  // ===================================================================================

  // FUNCTIONALITY: Clamps the input score to a 0-100 range to prevent visual errors.
  const normalizedScore = Math.min(100, Math.max(0, score));
  
  // APPEARANCE: Calculates the radius and circumference of the gauge's arc.
  const radius = (size / 2) - (strokeWidth * 1.5);
  const circumference = 2 * Math.PI * radius;
  
  // APPEARANCE: Calculates the SVG stroke offset to visually represent the score.
  // The divisor (1.8) adjusts the arc to fit the semicircle shape correctly.
  const offset = circumference - (normalizedScore / 100) * (circumference / 1.8);
  
  // ===================================================================================
  //  APPEARANCE HELPERS
  //  Functions that determine the visual style of the gauge.
  // ===================================================================================

  // APPEARANCE: Determines the color of the gauge based on the score.
  const getColor = (score) => {
  if (score < 10) return '#8B0000'; // Deep Red (0-9)
  if (score < 20) return '#EF4444'; // Light Red (10-19)
  if (score < 40) return '#F97316'; // Orange (20-39)
  if (score < 60) return '#FACC15'; // Yellow (40-59)
  if (score < 80) return '#A3E635'; // Yellow-Green (60-79)
  return '#22C55E'; // Green (80-100)
};
  
  const color = getColor(normalizedScore);
  
  // APPEARANCE: Returns the descriptive text rating (e.g., 'Poor', 'Excellent') for a given score.
  const getEfficiencyRating = (score) => {
    if (t) {
      if (score < 20) return t('efficiencyRating_poor');
      if (score < 40) return t('efficiencyRating_moderate');
      if (score < 60) return t('efficiencyRating_fair');
      if (score < 80) return t('efficiencyRating_good');
      return t('efficiencyRating_excellent');
    } else {
      if (score < 20) return 'Poor';
      if (score < 40) return 'Moderate';
      if (score < 60) return 'Fair';
      if (score < 80) return 'Good';
      return 'Excellent';
    }
  };

  
  // ===================================================================================
  //  RENDER
  //  The JSX structure that defines the component's appearance.
  // ===================================================================================
  return (
    <div className="flex flex-col items-center justify-center p-6">
      {/* Container for the SVG and the text overlay */}
      <div className="relative" style={{ width: size, height: size / 2 + strokeWidth }}>
        <svg 
          width={size} 
          height={size / 2 + strokeWidth} 
          viewBox={`0 0 ${size} ${size / 2 + strokeWidth}`}
          className="overflow-visible"
        >
          {/* Background arc */}
          <path
            d={`M${strokeWidth/2},${size / 2} A${radius + strokeWidth/2},${radius + strokeWidth/2} 0 0,1 ${size - strokeWidth/2},${size / 2}`}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {/* Foreground arc (progress) */}
          <path
            d={`M${strokeWidth/2},${size / 2} A${radius + strokeWidth/2},${radius + strokeWidth/2} 0 0,1 ${size - strokeWidth/2},${size / 2}`}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        
        {/* Rating display */}
        <div className="absolute bottom-0 left-0 right-0 text-center">
          <div className="text-3xl font-bold" style={{ color }}>
            {getEfficiencyRating(normalizedScore)}
          </div>
        </div>
      </div>
      

    </div>
  );
};

export default EfficiencyGauge;
